import { BaasicDropdownStore, BaseEditViewStore } from 'core/stores';
import { NikoPatientInsuranceEditForm } from 'application/niko-patient/forms';
import { InsuranceService } from 'application/import-reference/services';
import { action } from "mobx";
class NikoPatientInsuranceEditViewStore extends BaseEditViewStore {
    constructor(rootStore, componentProps) {
        const pageViewStore = componentProps.pageViewStore;
        const keyPrefix = componentProps.keyPrefix;
        const action = componentProps.action;
        super(rootStore, {
            name: 'niko-patient-insurance-edit',
            Form: NikoPatientInsuranceEditForm,
            id: pageViewStore.patientInsurance[`${keyPrefix}Id`],
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async () => {
                        if (action == "Edit") {
                            return {
                                insuranceId: pageViewStore.patientInsurance[`${keyPrefix}InsuranceId`],
                                policyNo: pageViewStore.patientInsurance[`${keyPrefix}PolicyNo`],
                                startDate: pageViewStore.patientInsurance[`${keyPrefix}StartDate`],
                                endDate: pageViewStore.patientInsurance[`${keyPrefix}EndDate`],
                                relationship: pageViewStore.patientInsurance[`${keyPrefix}Relationship`],
                                insuredFirstName: pageViewStore.patientInsurance[`${keyPrefix}InsuredFirstName`],
                                insuredLastName: pageViewStore.patientInsurance[`${keyPrefix}InsuredLastName`],
                                insuredDOB: pageViewStore.patientInsurance[`${keyPrefix}InsuredDOB`],
                                insuredGender: pageViewStore.patientInsurance[`${keyPrefix}InsuredGender`],
                            };
                        }

                        return {};
                    },
                    create: async (resource) => {
                        await pageViewStore.nikoPatientDetailInsuranceService.create(resource);
                    },
                    update: async (resource) => {
                        await pageViewStore.nikoPatientDetailInsuranceService.update(resource);
                    }
                }
            },
            FormClass: NikoPatientInsuranceEditForm
        });

        this.rootStore = rootStore;
        this.pageViewStore = pageViewStore;
        this.isEditAction = action == "Edit";
        this.isChangeAction = action == "Change";
        this.isPrimary = keyPrefix == 'primaryActive' || keyPrefix == 'primaryInactive';
        this.isActive = keyPrefix == 'primaryActive' || keyPrefix == 'secondaryActive';
        this.title = this.isPrimary ?
            (this.isActive ? `${action} Primary Insurance Information` : `${action} Inactive Medicare Policy`) :
            (this.isActive ? `${action}  Secondary Insurance Information` : `${action} Inactive Medicaid Policy`);

        const insuranceService = new InsuranceService(rootStore.application.baasic.apiClient);
        this.insuranceStore = new BaasicDropdownStore({
            filterable: true,
        }, {
            fetchFunc: async (term) => {
                if (!term && this.isEditAction) {
                    return [pageViewStore.patientInsurance[`${keyPrefix}Insurance`]];
                }

                const response = await insuranceService.find({
                    searchQuery: term,
                    isNiko: true,
                    isActive: true,
                    rpp: 2500,
                });

                if (keyPrefix == 'primaryInactive') {
                    return response.data.item.filter(x => x.name == "Medicare");
                }
                else if (keyPrefix == 'secondaryInactive') {
                    return response.data.item.filter(x => x.name.toLowerCase().includes("medicaid") && !x.name.toLowerCase().includes("do not use") && !x.name.toLowerCase().includes("(medicaid"));
                }
                return response.data.item;
            }
        });

        this.relationshipStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{ name: 'Self' }, { name: 'Spouse' }, { name: 'Parent' }, { name: 'Employer' }, { name: 'Other' }];
            }
        });

        this.genderStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{ name: 'Male' }, { name: 'Female' }];
            }
        });

        if (!this.isEditAction)
            this.form.$("relationship").set('value', 'Self');

        if (this.isChangeAction) {
            this.oldStartDate = pageViewStore.patientInsurance[`${keyPrefix}StartDate`];
            const existingOldEndDate = pageViewStore.patientInsurance[`${keyPrefix}EndDate`];
            if (existingOldEndDate) {
                this.form.$("oldEndDate").set('value', existingOldEndDate);
            }
            // Require oldEndDate by default; the checkbox in the template clears the rule when "End Date Unavailable"
            // is checked.
            this.form.$("oldEndDate").set('rules', 'required');
        }

        this.form.observe({
            path: "relationship",
            key: "value",
            call: ({ change }) => this.onRelationshipChange(change),
        });

        this.form.observe({
            path: "oldEndDateUnavailable",
            key: "value",
            call: ({ change }) => this.onOldEndDateUnavailableChange(change),
        });

        this.applyRelationshipRules();
    }

    @action.bound
    onOldEndDateUnavailableChange(change) {
        if (!change) return;
        if (change.newValue === true) {
            this.form.$("oldEndDate").set('value', null);
        }
    }

    @action
    async initialize() {
        if (this.isEdit) {
            await this.getResource(this.id);
        }
        else {
            this.form.clear();
        }
        this.applyRelationshipRules();
    }

    @action.bound
    onRelationshipChange(change) {
        // Skip initial value set (oldValue is undefined/null on first set during load).
        if (!change || change.oldValue === undefined || change.oldValue === null || change.oldValue === "") {
            this.applyRelationshipRules();
            return;
        }
        if (change.oldValue === change.newValue) return;
        this.form.$("insuredFirstName").set("value", "");
        this.form.$("insuredLastName").set("value", "");
        this.form.$("insuredDOB").set("value", "");
        this.form.$("insuredGender").set("value", "");
        this.genderStore.setValue(null);
        this.applyRelationshipRules();
    }

    @action.bound
    applyRelationshipRules() {
        const relationship = this.form.$("relationship").value;
        const requiresInsured = relationship === "Spouse" || relationship === "Parent" || relationship === "Other";
        const requiresEmployer = relationship === "Employer";

        // Insured DOB cannot be in the future (today or earlier). The before_or_equal_date rule also
        // caps the DatePicker's max (see DatePickerFieldTemplate), preventing future selection.
        const maxDobRule = "before_or_equal_date:" + new Date().toISOString().split('T')[0];

        this.form.$("insuredFirstName").set("rules", requiresInsured ? "required|string" : "string");
        this.form.$("insuredLastName").set("rules", (requiresInsured || requiresEmployer) ? "required|string" : "string");
        this.form.$("insuredDOB").set("rules", requiresInsured ? "required|" + maxDobRule : maxDobRule);
        this.form.$("insuredGender").set("rules", requiresInsured ? "required|string" : "string");

        this.form.$("insuredLastName").set("label", requiresEmployer ? "Employer Name" : "Insured Last Name");
        this.form.$("insuredLastName").set("placeholder", requiresEmployer ? "Enter Employer Name" : "Enter Insured Last Name");
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        if (resource.startDate && resource.endDate && new Date(resource.endDate) <= new Date(resource.startDate)) {
            this.rootStore.notificationStore.error("End Date must be greater than Start Date.");
            return;
        }
        
        if (this.isChangeAction && !this.isOldEndDateValid(resource)) {
            this.rootStore.notificationStore.error("Enter an end date for the previous insurance or check End Date Unavailable.");
            return;
        }

        if (this.isChangeAction && !resource.oldEndDateUnavailable && this.oldStartDate && resource.oldEndDate && new Date(resource.oldEndDate) <= new Date(this.oldStartDate)) {
            this.rootStore.notificationStore.error("End Date for the previous insurance must be greater than its start date.");
            return;
        }

        if (this.isPrimary && this.isActive && !this.isEditAction && this.pageViewStore.nikoPatientDetail.eligibilityStatus && this.pageViewStore.nikoPatientDetail.eligibilityStatus.name === "Eligible") {
            let nikoPatientTemplates = (await this.pageViewStore.nikoPatientTemplateService.find({
                nikoPatientDetailId: this.pageViewStore.nikoPatientDetail.id,
                facilityOrderId: this.pageViewStore.nikoPatientDetail.facilityOrderId,
                embed: "ClinicalStatus",
                fileds: "ClinicalStatus",
            })).data.item;

            if (nikoPatientTemplates.some(x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required"))) {
                this.pageViewStore.insuranceChangeConfirmModal.onConfirm = async () => {
                    this.pageViewStore.insuranceChangeConfirmModal.loading = true;
                    await this.continueUpdateResource(resource);
                    this.pageViewStore.insuranceChangeConfirmModal.loading = false;
                    this.pageViewStore.insuranceChangeConfirmModal.close();
                };
                this.pageViewStore.insuranceChangeConfirmModal.open({
                    message: "After the patient's primary active insurance gets changed, the PARs will be removed from all of the patient’s templates."
                });
                return;
            }
        }
        await this.continueUpdateResource(resource);
    }

    @action.bound
    async continueUpdateResource(resource) {
        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            if (this.isChangeAction) {
                // Swap: create the new insurance and close out the old one (this.id). In Niko the old insurance's
                // end date is updated and then it is deleted; a new insurance is created. Portal deletes the old record.
                const newInsurance = {
                    ...resource,
                    nikoPatientDetailId: this.pageViewStore.nikoPatientDetail.id,
                    type: this.isPrimary ? "Primary" : "Secondary",
                    status: this.isActive ? "Active" : "Inactive",
                    oldEndDate: resource.oldEndDateUnavailable ? null : resource.oldEndDate,
                };
                delete newInsurance.id;
                delete newInsurance.oldEndDateUnavailable;

                await this.pageViewStore.nikoPatientDetailInsuranceService.swap(this.id, newInsurance);
            } else {
                const payload = { ...resource, id: this.id, nikoPatientDetailId: this.pageViewStore.nikoPatientDetail.id, type: this.isPrimary ? "Primary" : "Secondary", status: this.isActive ? "Active" : "Inactive", action: this.isEditAction ? "Edit" : "" };
                delete payload.oldEndDate;
                delete payload.oldEndDateUnavailable;

                await this.actions.update(payload);
            }
            await setTimeout(() => this.notifySuccessUpdate(this.name), 10);
        }
        catch (err) {
            this.onUpdateError(err);
        }
        finally {
            this.pageViewStore.initialize(true);
            this.form.setFieldsDisabled(false);
        }
    }

    isOldEndDateValid(resource) {
        if (resource.oldEndDateUnavailable) return true;
        return !!resource.oldEndDate;
    }

    @action.bound
    async createResource(resource) {
        if (!this.actions.create) return;
        if (this.isPrimary && this.isActive && this.pageViewStore.nikoPatientDetail.eligibilityStatus && this.pageViewStore.nikoPatientDetail.eligibilityStatus.name === "Eligible") {
            let nikoPatientTemplates = (await this.pageViewStore.nikoPatientTemplateService.find({
                nikoPatientDetailId: this.pageViewStore.nikoPatientDetail.id,
                facilityOrderId: this.pageViewStore.nikoPatientDetail.facilityOrderId,
                embed: "ClinicalStatus",
                fileds: "ClinicalStatus",
            })).data.item;

            if (nikoPatientTemplates.some(x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required"))) {
                this.pageViewStore.insuranceChangeConfirmModal.onConfirm = async () => {
                    this.pageViewStore.insuranceChangeConfirmModal.loading = true;
                    await this.continueCreateResource(resource);
                    this.pageViewStore.insuranceChangeConfirmModal.loading = false;
                    this.pageViewStore.insuranceChangeConfirmModal.close();
                };
                this.pageViewStore.insuranceChangeConfirmModal.open({
                    message: "After the patient's primary active insurance gets changed, the PARs will be removed from all of the patient’s templates."
                });
                return;
            }
        }
        await this.continueCreateResource(resource);
    }

    @action.bound
    async continueCreateResource(resource) {
        if (resource.startDate && resource.endDate && new Date(resource.endDate) <= new Date(resource.startDate)) {
            this.rootStore.notificationStore.error("End Date must be greater than Start Date.");
            return;
        }

        this.form.setFieldsDisabled(true);

        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }
            const payload = { ...resource, nikoPatientDetailId: this.pageViewStore.nikoPatientDetail.id, type: this.isPrimary ? "Primary" : "Secondary", status: this.isActive ? "Active" : "Inactive" };
            delete payload.oldEndDate;
            delete payload.oldEndDateUnavailable;
            await this.actions.create(payload);
            await setTimeout(() => this.notifySuccessCreate(), 10);
        }
        catch (err) {
            this.onCreateError(err);
        }
        finally {
            this.pageViewStore.initialize(true);
            this.form.setFieldsDisabled(false);
        }
    }
}

export default NikoPatientInsuranceEditViewStore;